import * as React from 'react';
import type { ConnectedProps } from 'react-redux';
import { connect } from 'react-redux';
import type { CustomRouteComponentProps } from 'react-router-dom';
import { compose } from 'redux';
import type { InjectedFormProps } from 'redux-form';
import { reduxForm } from 'redux-form';

import type SaleRequestModel from 'ab-requestModels/sale.requestModel';

import type { RootState } from 'af-reducers';

import { toRequestModelWithRawPhoneNumbers } from 'af-utils/form.util';

import { SALE_EDIT } from 'af-constants/reduxForms';
import CLIENT from 'af-constants/routes/client';

import Breadcrumbs from 'af-components/Breadcrumbs';
import EditSaleForm from 'af-components/SharedForms/Resources/Sale';
import { validate, warn } from 'af-components/SharedForms/Resources/Sale/validation';

import * as SaleActions from 'af-actions/sale';

interface PathParams {
	id: string;
}

type OwnProps = CustomRouteComponentProps<PathParams>;

type FormProps = InjectedFormProps<SaleRequestModel, FormOwnProps>;

type FormOwnProps = OwnProps & ConnectedProps<typeof connector>;

type Props = FormOwnProps & FormProps;

const EditSale: React.FC<Props> = (props) => {
	const { updateSale, location: { state: { orgAlias } }, match: { params: { id } }, companyName, history, initialize, findById } = props;

	// On mount
	React.useEffect(() => {
		const getData = async () => {
			const sale = await findById(id);
			initialize(sale);
		};
		getData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const editSale = React.useCallback(async (form: SaleRequestModel) => {
		const data: SaleRequestModel = toRequestModelWithRawPhoneNumbers(form, 'phone', 'fax');
		await updateSale(data);
		history.push(CLIENT.COMPANY.RESOURCES.SALE.LIST(orgAlias, companyName));
	}, [companyName, history, orgAlias, updateSale]);

	return (
		<div className="form-segment">
			<Breadcrumbs
				items={
					[
						{ label: 'Sales', url: CLIENT.COMPANY.RESOURCES.SALE.LIST(orgAlias, companyName) },
						{ label: 'Preview', url: CLIENT.COMPANY.RESOURCES.SALE.PREVIEW(id, orgAlias, companyName) },
						{ label: 'Edit' },
					]
				}
			/>
			<EditSaleForm {...props} onSubmit={editSale} />
		</div>
	);
};

function mapStateToProps(state: RootState) {
	const { companyData } = state.user;
	if (!companyData) {
		throw new Error('User not logged in');
	}

	return {
		companyName: companyData.name,
	};
}

function mapDispatchToProps() {
	return {
		findById: SaleActions.findById,
		updateSale: SaleActions.update,
	};
}

const connector = connect(mapStateToProps, mapDispatchToProps());

const enhance = compose<React.ComponentClass<OwnProps>>(
	React.memo,
	connector,
	reduxForm<SaleRequestModel, FormOwnProps>({ form: SALE_EDIT, validate, warn })
);

export default enhance(EditSale);
